import {
  of,
  map,
  filter,
  timeout,
  Observable,
  combineLatest,
  firstValueFrom,
  distinctUntilChanged,
} from "rxjs";
import {
  SecurityCheckinModel,
  SecuritySiteCountryModel,
  SecurityCountryFlagModel,
  SecurityCheckinMemberModel,
  SecurityCountryOutlineModel,
  SecurityCheckinOptionsModel,
  SecurityCountryLocationModel,
  SecurityMappingFocusRequestModel,
  SecurityCheckinCountryOptionModel,
} from "src/app/store/models/security-mapping.model";
import {
  UserModel,
  RegionModel,
  UserRolesModel,
  OpcoResponseModel,
} from "src/app/store/models/user.model";
import {
  getSecurityCountryFlag,
  SECURITY_FALLBACK_COUNTRY_FLAG,
} from "src/app/utils/security-mapping/security-mapping.utils";
import {
  LocationSelectValueModel,
  LocationSelectOptionModel,
} from "src/app/store/models/location-select.model";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { SiteModel } from "src/app/store/models/asset.model";
import { RoleTypeEnum } from "src/app/store/models/role.model";
import { toSignal, toObservable } from "@angular/core/rxjs-interop";
import { inject, signal, computed, Injectable } from "@angular/core";
import { HttpService } from "src/app/services/http-service/http-client.service";
import { StorageService } from "src/app/services/storage-service/storage.service";
import { SecurityMappingCountryService } from "./security-mapping-country.service";
import { FirebaseService } from "src/app/services/firebase-service/firebase.service";
import { NotificationData } from "src/app/store/models/notification.model";
import { SecurityMappingFirestoreService } from "./security-mapping-firestore.service";

const CHECKIN_KEY_PREFIX = "security_mapping_checkin";
// Expiry reminder push tapped on a cold start: longest wait for the checked-in site
const SITE_WAIT_MS = 10 * 1000;
// Option id of a combined country (BOG countries sharing a group)
const COMBINED_COUNTRY_ID_PREFIX = "group:";

// Country check-in of Security mapping (developer's request 2026-10-07; mockups v1.37):
// the country whose points, areas, routes and map are shown. Region and country lists
// come from the BOG region and opco APIs (as Admin → User management and Reports);
// applying a country reads its active sites (getSitesByOpcoId: isActive) for the same
// country / region objects the checked-in site would give, so the data keys
// (countries/{location.country.unique_id}) don't change. The choice is kept on the
// device per tenant database and stays when another site is checked in.
@Injectable({ providedIn: "root" })
export class SecurityMappingCheckinService {
  private store = inject(Store);
  private router = inject(Router);
  private http = inject(HttpService);
  private storage = inject(StorageService);
  private firebase = inject(FirebaseService);
  private securityMappingCountry = inject(SecurityMappingCountryService);
  private securityMappingFirestore = inject(SecurityMappingFirestoreService);

  // The applied country; null: the checked-in site's own country
  readonly checkin = signal<SecurityCheckinModel | null>(null);
  // The stored check-in was read: the data waits for it, so it loads one country only
  private readonly isRestored = signal<boolean>(false);
  private readonly site = toSignal<SiteModel | null>(
    this.store.select("site"),
    {
      initialValue: null,
    },
  );
  private optionsRequest: Promise<SecurityCheckinOptionsModel> | null = null;
  // Every active country, without the role filter (Mapping page)
  private allOptionsRequest: Promise<SecurityCheckinOptionsModel> | null = null;
  // TODO: remove — temporary: last flags API response, logged on every LoadOptions
  private flagsResponseForLog: unknown = null;
  // Ignores a restore that finished after a newer one started (another login)
  private restoreRequest = 0;
  // Expiry reminder tapped: the item the Security mapping page opens (kept here, not in
  // the data service, so the bell panel doesn't start loading Security mapping data)
  readonly focusRequest = signal<SecurityMappingFocusRequestModel | null>(null);
  // Push tapped on a cold start: the stored check-in and the site load first
  private readonly restored$ = toObservable(this.isRestored).pipe(
    filter((isRestored) => isRestored),
  );
  private readonly site$ = toObservable(this.site).pipe(
    filter((site) => !!site?.id),
  );

  // Shown in Settings: the applied country, else the checked-in site's
  readonly countryName = computed<string>(
    () =>
      this.checkin()?.countryName ??
      this.securityMappingCountry.GetSiteCountryName(this.site()),
  );

  // Country + region the data is loaded and saved under
  readonly location$: Observable<SecurityCountryLocationModel | null> =
    combineLatest([
      this.store.select("site") as Observable<SiteModel | null>,
      toObservable(this.checkin),
      toObservable(this.isRestored),
    ]).pipe(
      filter(([, , isRestored]) => isRestored),
      map(
        ([site, checkin]) => checkin?.location ?? getSiteCountryLocation(site),
      ),
    );

  // BOG regions (region API), for the group the breadcrumb shows; loaded once per login
  private readonly bogRegions = signal<RegionModel[]>([]);
  // The check-in lists once loaded (LoadOptions): the breadcrumb's region row name
  private readonly loadedOptions = signal<SecurityCheckinOptionsModel | null>(null);
  // LoadOptions failed: the breadcrumb falls back to the site's own region
  private readonly hasOptionsFailed = signal<boolean>(false);

  // The breadcrumb's region group isn't known yet (neither the regions nor the lists
  // loaded): a placeholder instead of the site's own region name, which would flash first
  // (developer's request 2026-11-04)
  readonly isRegionNameLoading = computed<boolean>(
    () =>
      !this.checkin()?.regionName?.trim() &&
      !this.loadedOptions() &&
      !this.bogRegions().length &&
      !this.hasOptionsFailed(),
  );

  // Region step of the Settings breadcrumb (developer's request 2026-10-19), shown as its
  // group like the country picker ("Asia" → "AMEA"): the row saved with the check-in;
  // else the row the picker opens on, once the lists loaded; else the applied / checked-in
  // site's region looked up in the BOG regions; else that region's own name
  readonly regionName = computed<string>(() => {
    const savedName = this.checkin()?.regionName?.trim();
    if (savedName) return savedName;
    if (this.isRegionNameLoading()) return "";
    const options = this.loadedOptions();
    if (options) {
      const { regionId } = this.GetSelectedValue(options);
      const row = options.regions.find((region) => region.id === regionId);
      if (row?.name) return row.name.trim();
    }
    const region =
      this.checkin()?.location.region ??
      getSiteCountryLocation(this.site())?.region;
    const name = (region?.aliasName ?? region?.location ?? "").trim();
    const names = [region?.aliasName, region?.location]
      .map((item) => (item ?? "").trim().toLowerCase())
      .filter(Boolean);
    const uniqueId = (region?.unique_id ?? "").trim();
    const bogRegion =
      this.bogRegions().find(
        (item) => !!uniqueId && (item.uniqueId ?? "").trim() === uniqueId,
      ) ??
      this.bogRegions().find((item) =>
        names.includes((item.name ?? "").trim().toLowerCase()),
      );
    return bogRegion?.group?.trim() || name;
  });
  // TODO: remove — temporary: last region / opco (country) API responses, logged on
  // every LoadOptions
  private regionResponseForLog: unknown = null;
  private opcoResponseForLog: unknown = null;
  // Every country the data is loaded from: a combined check-in's members (e.g.
  // Australia + New Zealand for "AUS & NZ"), else the one country of location$
  readonly locations$: Observable<SecurityCountryLocationModel[]> =
    combineLatest([
      this.store.select("site") as Observable<SiteModel | null>,
      toObservable(this.checkin),
      toObservable(this.isRestored),
    ]).pipe(
      filter(([, , isRestored]) => isRestored),
      map(([site, checkin]) => {
        if (checkin?.members?.length) {
          return checkin.members.map((member) => member.location);
        }
        const location = checkin?.location ?? getSiteCountryLocation(site);
        return location ? [location] : [];
      }),
    );
  // Check-ins already looked up for their group's countries (withMemberNames)
  private memberNamesCheckedIds = new Set<string>();

  constructor() {
    // Each login (tenant) has its own stored check-in and its own country options
    this.store
      .select("user")
      .pipe(
        map((user: UserModel | null) => user?.email ?? ""),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.optionsRequest = null;
        this.allOptionsRequest = null;
        this.loadedOptions.set(null);
        this.hasOptionsFailed.set(false);
        void this.restore();
        void this.loadBogRegions();
      });
  }

  // Regions that have countries, and the countries (one per BOG country id, with its
  // opcos); loaded once per session
  public LoadOptions(): Promise<SecurityCheckinOptionsModel> {
    this.optionsRequest ??= this.fetchOptions(true)
      .then((options) => {
        this.loadedOptions.set(options);
        this.hasOptionsFailed.set(false);
        return options;
      })
      .catch((error: unknown) => {
        this.optionsRequest = null;
        this.hasOptionsFailed.set(true);
        throw error;
      });
    // TODO: remove — temporary log to check the flags API data
    void this.optionsRequest
      .then(() =>
        console.warn(
          "Security mapping: country flags API response",
          this.flagsResponseForLog,
        ),
      )
      .catch(() => undefined);
    // TODO: remove — temporary log to check the region and country API data
    void this.optionsRequest
      .then((options) => {
        console.warn(
          "Security mapping: region API response",
          this.regionResponseForLog,
        );
        console.warn(
          "Security mapping: country (opco) API response",
          this.opcoResponseForLog,
        );
        console.warn("Security mapping: check-in options", options);
      })
      .catch((error: unknown) =>
        console.warn("Security mapping: region / country API failed", error),
      );
    return this.optionsRequest;
  }

  // The breadcrumb's group names; a failed call keeps the region names
  private async loadBogRegions(): Promise<void> {
    try {
      const response = await firstValueFrom(this.http.GetBogRegion());
      this.bogRegions.set((response?.data as RegionModel[] | undefined) ?? []);
    } catch (error) {
      console.warn("Security mapping: region API failed (breadcrumb group)", error);
    }
  }

  // Same regions and countries as LoadOptions, every active country whatever the user's
  // roles (Mapping page search / breadcrumb: open to every role for now); once per session
  public LoadAllOptions(): Promise<SecurityCheckinOptionsModel> {
    this.allOptionsRequest ??= this.fetchOptions(false).catch(
      (error: unknown) => {
        this.allOptionsRequest = null;
        throw error;
      },
    );
    return this.allOptionsRequest;
  }

  // The picker opens on the applied country, else on the checked-in site's region and
  // country (by its opco; developer's request 2026-10-16)
  public GetSelectedValue(
    options: SecurityCheckinOptionsModel,
  ): LocationSelectValueModel {
    const checkin = this.checkin();
    if (checkin?.regionId && checkin.countryId) {
      // The region row the country is listed under now (regions merge by group)
      const option = options.countries.find(
        (item) => item.id === checkin.countryId,
      );
      return {
        regionId: option?.parentId ?? checkin.regionId,
        countryId: checkin.countryId,
        siteId: null,
      };
    }
    const opcoId = this.site()?.locations?.opco?.id?.toLowerCase();
    const country = opcoId
      ? options.countries.find((option) =>
          option.opcoIds.some((id) => id.toLowerCase() === opcoId),
        )
      : undefined;
    return {
      regionId: country?.parentId ?? null,
      countryId: country?.id ?? null,
      siteId: null,
    };
  }

  // false when the country has no active site with a country (nothing to load)
  public async Apply(
    value: LocationSelectValueModel,
    options: SecurityCheckinOptionsModel,
  ): Promise<boolean> {
    const country = options.countries.find(
      (option) => option.id === value.countryId,
    );
    if (!country || !value.regionId) return false;
    const sites = await firstValueFrom(
      this.firebase.getSitesByOpcoId(country.opcoIds),
    );
    // One member per country the sites are in (a combined option has several)
    const memberById = new Map<string, SecurityCheckinMemberModel>();
    sites.forEach((site) => {
      const location = getSiteCountryLocation(site ?? null);
      const countryId = location?.country.unique_id.trim();
      if (!location || !countryId || memberById.has(countryId)) return;
      memberById.set(countryId, {
        location,
        coordinate: getSiteCoordinate(site),
      });
    });
    const members = Array.from(memberById.values());
    const [first] = members;
    if (!first) return false;

    const regionName = options.regions.find(
      (region) => region.id === value.regionId,
    )?.name;
    const checkin: SecurityCheckinModel = {
      location: first.location,
      regionId: value.regionId,
      ...(regionName ? { regionName } : {}),
      countryId: country.id,
      countryName: country.name,
      coordinate: first.coordinate,
      ...(members.length > 1 ? { members } : {}),
      ...(country.memberCountryNames?.length
        ? { memberCountryNames: country.memberCountryNames }
        : {}),
    };
    this.checkin.set(checkin);
    await this.storage.set(await this.getStorageKey(), checkin);
    return true;
  }

  // Expiry reminder tapped (developer's request 2026-10-08): the item's country becomes
  // the applied one, from countries/{countryId} (the country + region its data is saved
  // under); the checked-in site's own country clears the check-in. false: no such country
  public async CheckInToCountry(
    countryId: string,
    coordinate: google.maps.LatLngLiteral | null,
  ): Promise<boolean> {
    const siteLocation = getSiteCountryLocation(this.site());
    const currentId =
      this.checkin()?.location.country.unique_id ??
      siteLocation?.country.unique_id;
    if (currentId === countryId) return true;
    // A member of the applied combined country: its data is already loaded
    const isMember = (this.checkin()?.members ?? []).some(
      (member) => member.location.country.unique_id === countryId,
    );
    if (isMember) return true;
    if (siteLocation?.country.unique_id === countryId) {
      this.checkin.set(null);
      await this.storage.remove(await this.getStorageKey());
      return true;
    }

    const location =
      await this.securityMappingFirestore.GetCountryLocation(countryId);
    if (!location) return false;
    const countryName = (
      location.country.aliasName ??
      location.country.location ??
      ""
    ).trim();
    // The picker reopens on the BOG country of the same name (none: nothing selected)
    const options = await this.LoadOptions().catch(() => null);
    const option = options?.countries.find(
      (item) => item.name.trim().toLowerCase() === countryName.toLowerCase(),
    );
    const regionName = options?.regions.find(
      (region) => region.id === option?.parentId,
    )?.name;
    const checkin: SecurityCheckinModel = {
      location,
      coordinate,
      countryName,
      countryId: option?.id ?? "",
      regionId: option?.parentId ?? "",
      ...(regionName ? { regionName } : {}),
    };
    this.checkin.set(checkin);
    await this.storage.set(await this.getStorageKey(), checkin);
    return true;
  }

  // Expiry reminder tapped (bell panel or push): checks in to the item's country, then
  // the Security mapping page opens the item, focused on the map
  public async OpenReminderItem(
    data: NotificationData | undefined,
  ): Promise<void> {
    if (!data?.documentId || !data.countryId) return;
    const lat = Number(data.lat);
    const lng = Number(data.lng);
    const position =
      data.lat && data.lng && Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lng }
        : null;
    // A restore finishing later would replace this check-in; without a site the page
    // can't open (both are instant once the app has started)
    const [, site] = await Promise.all([
      firstValueFrom(this.restored$),
      firstValueFrom(
        this.site$.pipe(timeout({ first: SITE_WAIT_MS, with: () => of(null) })),
      ),
    ]);
    try {
      await this.CheckInToCountry(data.countryId, position);
    } catch (error) {
      console.warn("Security mapping: country check-in failed", error);
    }
    this.focusRequest.set({
      itemId: data.documentId,
      requestedAt: Date.now(),
    });
    const siteId = site?.id;
    await this.router.navigate(
      siteId ? ["/settings/security-mapping", siteId] : ["/settings"],
    );
  }

  // Outline of the applied country, else of the checked-in site's
  public async GetCountry(
    site: SiteModel | null,
  ): Promise<SecuritySiteCountryModel | null> {
    const checkin = await this.withMemberNames(this.checkin());
    // TODO: remove — temporary log to check the outline's check-in
    console.warn("Security mapping: outline check-in", checkin);
    if (checkin?.members?.length || checkin?.memberCountryNames?.length) {
      return this.getCombinedCountry(
        checkin.countryName,
        checkin.members ?? [checkin],
        checkin.memberCountryNames ?? [],
      );
    }
    return checkin
      ? this.securityMappingCountry.GetCountry(
          checkin.location.country,
          checkin.coordinate,
        )
      : this.getSiteGroupCountry(site);
  }

  private async restore(): Promise<void> {
    const request = ++this.restoreRequest;
    const stored: SecurityCheckinModel | null = await this.storage.get(
      await this.getStorageKey(),
    );
    if (request !== this.restoreRequest) return;
    this.checkin.set(stored?.location?.country?.unique_id ? stored : null);
    this.isRestored.set(true);
  }

  private async fetchOptions(
    filterByAccess: boolean,
  ): Promise<SecurityCheckinOptionsModel> {
    const [regionResponse, opcoResponse, flags] = await Promise.all([
      firstValueFrom(this.http.GetBogRegion()),
      firstValueFrom(this.http.GetAllOpcoFromRegionCollection()),
      this.fetchCountryFlags(),
    ]);
    // TODO: remove — temporary log to check the region and country API data
    this.regionResponseForLog = regionResponse;
    this.opcoResponseForLog = opcoResponse;
    const allRegions: RegionModel[] = regionResponse?.data ?? [];
    const opcos: OpcoResponseModel[] = opcoResponse?.data ?? [];
    const regionById = new Map(
      allRegions
        .filter((region) => !!region?.id && !!region?.name)
        .map((region) => [region.id.toLowerCase(), region]),
    );

    const countryById = new Map<string, SecurityCheckinCountryOptionModel>();
    const groupByCountryId = new Map<string, string>();
    // Every country of each group, inactive ones too: a group option outlines them all
    // (developer's screenshot 2026-10-30: "AUS & NZ" listed New Zealand only)
    const countryNamesByGroup = new Map<string, Set<string>>();
    opcos.forEach((opco) => {
      const groupKey = getGroupKey(opco?.countryGroup);
      const countryName = opco?.countryName?.trim();
      if (!groupKey || !countryName) return;
      const names = countryNamesByGroup.get(groupKey) ?? new Set<string>();
      names.add(countryName);
      countryNamesByGroup.set(groupKey, names);
    });
    opcos.forEach((opco) => {
      const region = regionById.get(opco?.regionId?.toLowerCase() ?? "");
      if (!region || !opco?.countryId || !opco.countryName || !opco.opcoId) {
        return;
      }
      // Inactive countries aren't offered (developer's request 2026-10-23)
      if ((opco.countryStatus ?? "").trim().toLowerCase() !== "active") return;
      // Sites are queried by their exact opco id (Firestore "in" is case-sensitive)
      const country = countryById.get(opco.countryId);
      if (country) {
        if (!country.opcoIds.includes(opco.opcoId)) {
          country.opcoIds.push(opco.opcoId);
        }
        return;
      }
      // Own flag, else its country group's (New Zealand → "Australia & New Zealand")
      const flag =
        getSecurityCountryFlag(opco.countryName, flags) ??
        getSecurityCountryFlag(opco.countryGroup ?? "", flags);
      groupByCountryId.set(opco.countryId, opco.countryGroup ?? "");
      countryById.set(opco.countryId, {
        id: opco.countryId,
        // Its group ("AUS & NZ"), else the alias name, else the name (developer's
        // request 2026-10-22)
        name: getCountryOptionName(opco),
        countryName: opco.countryName,
        parentId: region.id,
        opcoIds: [opco.opcoId],
        countryNames: [opco.countryName],
        // No flag: the round placeholder, without a code
        icon: flag?.icon ?? SECURITY_FALLBACK_COUNTRY_FLAG,
        iconCaption: flag?.code,
      });
    });

    const allCountries = combineGroupedCountries(
      Array.from(countryById.values()),
      groupByCountryId,
      flags,
    );
    const groupedCountries = filterByAccess
      ? await this.filterCountriesByAccess(allCountries)
      : allCountries;
    // Regions sharing a group (Asia + MEA → "AMEA") are one row with all their
    // countries (developer's request 2026-10-23); a region without a group stays itself
    const regionOptionIdByRegionId = new Map(
      allRegions.map((region) => {
        const groupKey = getGroupKey(region.group);
        return [
          region.id,
          groupKey ? `${COMBINED_COUNTRY_ID_PREFIX}${groupKey}` : region.id,
        ];
      }),
    );
    const countryKeys = new Set<string>();
    const countries = groupedCountries
      .map((country) => {
        // Shown under a group name ("AUS & NZ"): all the group's countries
        const groupNames = countryNamesByGroup.get(getGroupKey(country.name));
        return {
          ...country,
          parentId:
            regionOptionIdByRegionId.get(country.parentId ?? "") ??
            country.parentId,
          ...(groupNames && groupNames.size > 1
            ? { memberCountryNames: Array.from(groupNames) }
            : {}),
        };
      })
      // A combined country listed under two regions of one group: once
      .filter((country) => {
        const key = `${country.parentId}/${country.id}`;
        if (countryKeys.has(key)) return false;
        countryKeys.add(key);
        return true;
      });
    const regionIds = new Set(countries.map((country) => country.parentId));
    // A region shows its group (developer's request 2026-10-22), else its name
    const regionOptionById = new Map<string, LocationSelectOptionModel>();
    allRegions.forEach((region) => {
      const id = regionOptionIdByRegionId.get(region.id) ?? region.id;
      if (!regionIds.has(id) || regionOptionById.has(id)) return;
      regionOptionById.set(id, {
        id,
        name: region.group?.trim() || region.name,
        parentId: null,
      });
    });
    return { regions: Array.from(regionOptionById.values()), countries };
  }

  // ** The countries the user may check in to (developer's request 2026-10-15):
  // ** the ones they hold country_security_expert for. super_user and
  // ** group_security_expert see all of them; a user with no such role sees the
  // ** country of the site they are checked in to.
  private async filterCountriesByAccess(
    countries: SecurityCheckinCountryOptionModel[],
  ): Promise<SecurityCheckinCountryOptionModel[]> {
    const user = await firstValueFrom(
      this.store.select("user") as Observable<UserModel | null>,
    );
    if (hasAllCountriesAccess(user)) return countries;

    const expertCountryIds = (user?.roles ?? [])
      .filter(
        (role: UserRolesModel) =>
          role?.role === RoleTypeEnum.CountrySecurityExpert,
      )
      .map((role: UserRolesModel) => (role.locationId ?? "").trim())
      .filter(Boolean);
    if (!expertCountryIds.length) return this.getSiteCountryOptions(countries);

    // The role keeps the site country id (locations.country.unique_id), the options
    // the BOG country id: the active sites of those countries bridge the two by opco
    const opcoIds = await this.getOpcoIdsOfCountries(expertCountryIds);
    return countries.filter((country) =>
      country.opcoIds.some((id) => opcoIds.has(id.toLowerCase())),
    );
  }

  // The opcos of the active sites of these countries, lowercased
  private async getOpcoIdsOfCountries(
    countryIds: string[],
  ): Promise<Set<string>> {
    const sites = await firstValueFrom(
      this.firebase.getSitesByCountryIds(countryIds),
    );
    return new Set(
      sites
        .map((site) => site?.locations?.opco?.id?.toLowerCase() ?? "")
        .filter(Boolean),
    );
  }

  // The option of the checked-in site's own country (none: nothing to check in to)
  private getSiteCountryOptions(
    countries: SecurityCheckinCountryOptionModel[],
  ): SecurityCheckinCountryOptionModel[] {
    const opcoId = this.site()?.locations?.opco?.id?.toLowerCase();
    if (!opcoId) return [];
    return countries.filter((country) =>
      country.opcoIds.some((id) => id.toLowerCase() === opcoId),
    );
  }

  // Country flags of the picker rows (developer's request 2026-10-16); a failed call
  // only leaves the rows without flags
  private async fetchCountryFlags(): Promise<SecurityCountryFlagModel[]> {
    try {
      const response: { flags?: SecurityCountryFlagModel[] } | null =
        await firstValueFrom(this.http.getCountryFlagsKey());
      this.flagsResponseForLog = response;
      return Array.isArray(response?.flags) ? response.flags : [];
    } catch (error) {
      // TODO: remove — temporary log to check the flags API data
      console.warn("Security mapping: country flags API failed", error);
      this.flagsResponseForLog = error;
      return [];
    }
  }

  // Keys include the tenant database, as the Security mapping data cache
  private async getStorageKey(): Promise<string> {
    const dbName = (await this.storage.get("dbName")) ?? "(default)";
    return `${CHECKIN_KEY_PREFIX}_${dbName}`;
  }

  // Each member country with its outline (null when it has none): items are saved
  // under the country they are in
  public async GetCountryOutlines(
    locations: SecurityCountryLocationModel[],
  ): Promise<SecurityCountryOutlineModel[]> {
    const members = this.checkin()?.members ?? [];
    return Promise.all(
      locations.map(async (location) => {
        const coordinate =
          members.find(
            (member) =>
              member.location.country.unique_id === location.country.unique_id,
          )?.coordinate ?? null;
        const outline = await this.securityMappingCountry.GetCountry(
          location.country,
          coordinate,
        );
        return { location, outline };
      }),
    );
  }

  // A check-in shown under a group name ("AUS & NZ") saved without memberCountryNames:
  // the group's countries' own names from the picker option, saved on the check-in
  private async withMemberNames(
    checkin: SecurityCheckinModel | null,
  ): Promise<SecurityCheckinModel | null> {
    if (
      !checkin?.countryId ||
      checkin.memberCountryNames?.length ||
      this.memberNamesCheckedIds.has(checkin.countryId)
    ) {
      return checkin;
    }
    // Once per country and session: most countries have no group to add
    this.memberNamesCheckedIds.add(checkin.countryId);
    const options = await this.LoadOptions().catch(() => null);
    // By id, else by the name shown: a check-in saved while the group was one country
    // ("newzealand") keeps that id after the group became a combined option
    const countryName = checkin.countryName.trim().toLowerCase();
    const option =
      options?.countries.find((item) => item.id === checkin.countryId) ??
      options?.countries.find(
        (item) =>
          !!item.memberCountryNames?.length &&
          item.name.trim().toLowerCase() === countryName,
      );
    const memberCountryNames = option?.memberCountryNames;
    if (!memberCountryNames?.length) return checkin;
    const updated: SecurityCheckinModel = { ...checkin, memberCountryNames };
    // Another country applied meanwhile: keep it
    if (this.checkin() === checkin) {
      this.checkin.set(updated);
      await this.storage.set(await this.getStorageKey(), updated);
    }
    return updated;
  }

  // Combined country: one outline with every member's polygons, named after the group.
  // Each BOG country of the group is outlined, also one with no site (developer's
  // screenshot 2026-10-30: "AUS & NZ" showed New Zealand only)
  private async getCombinedCountry(
    name: string,
    members: SecurityCheckinMemberModel[],
    memberCountryNames: string[],
  ): Promise<SecuritySiteCountryModel | null> {
    const found = await Promise.all([
      ...members.map((member) =>
        this.securityMappingCountry.GetCountry(
          member.location.country,
          member.coordinate,
        ),
      ),
      ...memberCountryNames.map((memberName) =>
        this.securityMappingCountry.GetCountryByName(memberName),
      ),
    ]);
    // A country found both by its site and by its name is drawn once
    const outlineByName = new Map<string, SecuritySiteCountryModel>();
    found.forEach((outline) => {
      if (outline && !outlineByName.has(outline.name)) {
        outlineByName.set(outline.name, outline);
      }
    });
    const outlines = Array.from(outlineByName.values());
    if (!outlines.length) return null;
    return {
      name,
      labelPosition: outlines[0].labelPosition,
      polygons: outlines.flatMap((outline) => outline.polygons),
    };
  }

  // No check-in: the site's country, or all of its group's countries when the site's
  // country is shown under a group name (developer's screenshot 2026-11-05: "AUS & NZ"
  // with New Zealand only after the stored check-in was cleared)
  private async getSiteGroupCountry(
    site: SiteModel | null,
  ): Promise<SecuritySiteCountryModel | null> {
    const siteCountryName =
      this.securityMappingCountry.GetSiteCountryName(site);
    const key = siteCountryName.toLowerCase();
    const options = key ? await this.LoadOptions().catch(() => null) : null;
    const memberCountryNames = options?.countries.find(
      (item) =>
        !!item.memberCountryNames?.length &&
        item.name.trim().toLowerCase() === key,
    )?.memberCountryNames;
    // TODO: remove — temporary log to check the site group outline
    console.warn("Security mapping: site group outline", {
      siteCountry: site?.locations?.country,
      siteCountryName,
      checkin: this.checkin(),
      groupOptions: options?.countries.filter(
        (item) =>
          !!item.memberCountryNames?.length ||
          item.id.startsWith(COMBINED_COUNTRY_ID_PREFIX),
      ),
      memberCountryNames,
    });
    return memberCountryNames?.length
      ? this.getCombinedCountry(siteCountryName, [], memberCountryNames)
      : this.securityMappingCountry.GetSiteCountry(site);
  }
}

// super_user comes from adminRole, group_security_expert from roles[]: both see
// every country of the check-in picker
function hasAllCountriesAccess(user: UserModel | null): boolean {
  if (!user) return false;
  if ((user.adminRole ?? "").trim() === RoleTypeEnum.SuperUser) return true;
  return (user.roles ?? []).some(
    (role: UserRolesModel) => role?.role === RoleTypeEnum.GroupSecurityExpert,
  );
}

// A site's country and region; null without a country id
function getSiteCountryLocation(
  site: SiteModel | null,
): SecurityCountryLocationModel | null {
  const country = site?.locations?.country;
  if (!country?.unique_id?.trim()) return null;
  return { country, region: site?.locations?.region ?? null };
}

function getSiteCoordinate(
  site: SiteModel | null,
): google.maps.LatLngLiteral | null {
  const coordinate = site?.locations?.site?.coordinate;
  return coordinate?.lat != null && coordinate?.lng != null
    ? { lat: coordinate.lat, lng: coordinate.lng }
    : null;
}

// Lowercased, trimmed: groups are matched whatever their case
function getGroupKey(group: string | undefined): string {
  return (group ?? "").trim().toLowerCase();
}

// A group that is only the country's own name ("PHILIPPINES") isn't shown
function getCountryOptionName(opco: OpcoResponseModel): string {
  const aliasName = opco.countryAliasName?.trim() || opco.countryName;
  const groupKey = getGroupKey(opco.countryGroup);
  const isOwnName = [aliasName, opco.countryName].some(
    (name) => getGroupKey(name) === groupKey,
  );
  return groupKey && !isOwnName ? opco.countryGroup.trim() : aliasName;
}

// BOG countries sharing a group (New Zealand + Australia → "AUS & NZ") become one option
// named after the group, with all their opcos (developer's request 2026-10-22); one per
// region its countries are in. A country alone in its group keeps its own option.
function combineGroupedCountries(
  countries: SecurityCheckinCountryOptionModel[],
  groupByCountryId: Map<string, string>,
  flags: SecurityCountryFlagModel[],
): SecurityCheckinCountryOptionModel[] {
  const countriesByGroup = new Map<string, SecurityCheckinCountryOptionModel[]>();
  countries.forEach((country) => {
    const groupKey = getGroupKey(groupByCountryId.get(country.id));
    if (!groupKey) return;
    countriesByGroup.set(groupKey, [
      ...(countriesByGroup.get(groupKey) ?? []),
      country,
    ]);
  });

  const result: SecurityCheckinCountryOptionModel[] = [];
  const addedGroups = new Set<string>();
  countries.forEach((country) => {
    const groupKey = getGroupKey(groupByCountryId.get(country.id));
    const members = countriesByGroup.get(groupKey) ?? [];
    if (members.length < 2) {
      result.push(country);
      return;
    }
    if (addedGroups.has(groupKey)) return;
    addedGroups.add(groupKey);

    const name = (groupByCountryId.get(country.id) ?? "").trim();
    const flag = getSecurityCountryFlag(name, flags);
    const opcoIds = Array.from(
      new Set(members.flatMap((member) => member.opcoIds)),
    );
    const countryNames = Array.from(
      new Set(members.flatMap((member) => member.countryNames ?? [])),
    );
    const regionIds = Array.from(
      new Set(members.map((member) => member.parentId)),
    );
    regionIds.forEach((regionId) =>
      result.push({
        name,
        opcoIds,
        countryNames,
        memberCountryNames: members.map(
          (member) => member.countryName ?? member.name,
        ),
        parentId: regionId,
        id: `${COMBINED_COUNTRY_ID_PREFIX}${groupKey}`,
        icon: flag?.icon ?? country.icon,
        iconCaption: flag?.code ?? country.iconCaption,
      }),
    );
  });
  return result;
}
